import React from "react";
import { useTranslation } from "react-i18next";
import { CardScoreGroup, RankedPlayer } from "@uno/protocol";
import Avatar from "component/Ui/Avatar";
import LevelBadge from "component/Ui/LevelBadge";
import { getCardContent } from "utils/CardContent";
import { CARD_HEIGHT, CARD_WIDTH } from "types/Card";
import getPublicAssetUrl from "utils/PublicAsset";

type CardData = { id: string; type: string };

const IDEAL_OVERLAP = 11;
const MIN_OVERLAP = 5;
const GROUP_KEYS: CardScoreGroup[] = ["numbers", "actions", "wilds"];

type ScoreCardLayout = {
    width: number;
    height: number;
    miniScale: number;
    avatarOverlap: number;
    borderWidth: number;
};

const DEFAULT_LAYOUT: ScoreCardLayout = {
    width: 200,
    height: 340,
    miniScale: 0.28,
    avatarOverlap: 28,
    borderWidth: 9,
};

const COMPACT_LAYOUT: ScoreCardLayout = {
    width: 180,
    height: 270,
    miniScale: 0.23,
    avatarOverlap: 23,
    borderWidth: 7,
};

const DENSE_LAYOUT: ScoreCardLayout = {
    width: 160,
    height: 210,
    miniScale: 0.17,
    avatarOverlap: 18,
    borderWidth: 6,
};

const ME_HALO = "0 0 14px 3px rgba(251, 191, 36, 0.6), 0 0 28px 6px rgba(251, 191, 36, 0.3)";

type RankTheme = {
    paper: string;
    text: string;
    textWeak: string;
    numberOutline: string;
    numberDeep: string;
    dividerColor: string;
};

const RANK_THEMES: Record<number, RankTheme> = {
    1: {
        paper: "#fef3c7",
        text: "text-amber-900",
        textWeak: "text-amber-900/25",
        numberOutline: "#b45309",
        numberDeep: "#78350f",
        dividerColor: "rgba(180,83,9,0.18)",
    },
    2: {
        paper: "#e2e8f0",
        text: "text-slate-800",
        textWeak: "text-slate-800/25",
        numberOutline: "#475569",
        numberDeep: "#1e293b",
        dividerColor: "rgba(71,85,105,0.18)",
    },
    3: {
        paper: "#fed7aa",
        text: "text-orange-900",
        textWeak: "text-orange-900/25",
        numberOutline: "#9a3412",
        numberDeep: "#431407",
        dividerColor: "rgba(154,52,18,0.18)",
    },
};

const DEFAULT_THEME: RankTheme = {
    paper: "#dbeafe",
    text: "text-blue-900",
    textWeak: "text-blue-900/25",
    numberOutline: "#1e40af",
    numberDeep: "#172554",
    dividerColor: "rgba(30,64,175,0.18)",
};

const getRankTheme = (rank: number): RankTheme => RANK_THEMES[rank] ?? DEFAULT_THEME;

const buildRankTextShadow = (theme: RankTheme) => {
    const { numberOutline: o, numberDeep: d } = theme;
    return `-1.5px -1.5px 0 ${o}, 1.5px -1.5px 0 ${o}, -1.5px 1.5px 0 ${o}, 1.5px 1.5px 0 ${o}, 0 3px 0 ${d}, 0 4px 6px rgba(0,0,0,0.35)`;
};

type StackLayout = {
    overlap: number;
    visibleCount: number;
    hiddenCount: number;
};

const computeStackLayout = (count: number, layout: ScoreCardLayout): StackLayout => {
    if(count <= 1) {
        return { overlap: 0, visibleCount: count, hiddenCount: 0 };
    }

    const miniWidth = CARD_WIDTH * layout.miniScale;
    const stackAvailableWidth = layout.width
        - layout.borderWidth * 2
        - 16 /* body mx-2 */
        - 16 /* group px-2 */;
    const idealStackWidth = miniWidth + (count - 1) * IDEAL_OVERLAP;

    if(idealStackWidth <= stackAvailableWidth) {
        return { overlap: IDEAL_OVERLAP, visibleCount: count, hiddenCount: 0 };
    }

    const adaptiveOverlap = (stackAvailableWidth - miniWidth) / (count - 1);

    if(adaptiveOverlap >= MIN_OVERLAP) {
        return { overlap: adaptiveOverlap, visibleCount: count, hiddenCount: 0 };
    }

    const totalSlots = Math.floor((stackAvailableWidth - miniWidth) / MIN_OVERLAP) + 1;
    const visibleCount = Math.max(1, totalSlots - 1);
    return { overlap: MIN_OVERLAP, visibleCount, hiddenCount: count - visibleCount };
};

type DisplayItem =
    | { kind: "card"; card: CardData; index: number }
    | { kind: "back"; index: number; count: number };

type GroupRender = {
    group: CardScoreGroup;
    subtotal: number;
    isEmpty: boolean;
    overlap: number;
    stackWidth: number;
    items: DisplayItem[];
};

const computeGroupRenders = (player: RankedPlayer, layout: ScoreCardLayout): GroupRender[] =>
    GROUP_KEYS.map((group) => {
        const { items: handItems, total } = player.hand[group];
        const isEmpty = handItems.length === 0;
        const stackLayout = computeStackLayout(handItems.length, layout);
        const miniWidth = CARD_WIDTH * layout.miniScale;

        const items: DisplayItem[] = handItems
            .slice(0, stackLayout.visibleCount)
            .map((card, index) => ({ kind: "card", card, index } as DisplayItem));

        if(stackLayout.hiddenCount > 0) {
            items.push({
                kind: "back",
                index: stackLayout.visibleCount,
                count: stackLayout.hiddenCount,
            });
        }

        const stackWidth = isEmpty
            ? miniWidth
            : miniWidth + Math.max(0, items.length - 1) * stackLayout.overlap;

        return {
            group,
            subtotal: total,
            isEmpty,
            overlap: stackLayout.overlap,
            stackWidth,
            items,
        };
    });

const stackSlotStyle = (
    index: number,
    overlap: number,
    zIndex: number,
    layout: ScoreCardLayout,
): React.CSSProperties => ({
    left: index * overlap,
    top: 0,
    width: CARD_WIDTH * layout.miniScale,
    height: CARD_HEIGHT * layout.miniScale,
    zIndex,
});

type MiniCardProps = {
    card: CardData;
    index: number;
    overlap: number;
    layout: ScoreCardLayout;
};

const MiniCard: React.FC<MiniCardProps> = ({ card, index, overlap, layout }) => {
    const content = getCardContent(card.type);

    return (
        <div
            className="ge-card-mini absolute"
            style={stackSlotStyle(index, overlap, index, layout)}
        >
            <div
                className="absolute top-0 left-0"
                style={{
                    transform: `scale(${layout.miniScale})`,
                    transformOrigin: "top left",
                }}
            >
                <div className="card-container">
                    <div className="card-inner">
                        <div className={`card ${card.type.toLowerCase()}`}>
                            <div>{content.corner}</div>
                            <div>{content.corner}</div>
                            <div>{content.center}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

type BackMiniCardProps = {
    index: number;
    overlap: number;
    count: number;
    layout: ScoreCardLayout;
};

const BackMiniCard: React.FC<BackMiniCardProps> = ({ index, overlap, count, layout }) => (
    <div
        className="ge-card-mini absolute"
        style={stackSlotStyle(index, overlap, index + 1000, layout)}
    >
        <div
            className="absolute top-0 left-0"
            style={{
                transform: `scale(${layout.miniScale})`,
                transformOrigin: "top left",
            }}
        >
            <div className="card-container">
                <div className="card-inner">
                    <div className="card back">
                        <div></div>
                        <div></div>
                        <div></div>
                    </div>
                </div>
            </div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span
                className="text-white font-changa-one leading-none"
                style={{ fontSize: 11, textShadow: "0 1px 2px rgba(0,0,0,0.85)" }}
            >
                +{count}
            </span>
        </div>
    </div>
);

type PlayerScoreCardProps = {
    player: RankedPlayer;
    rank: number;
    isMe?: boolean;
    isCompact?: boolean;
    isDense?: boolean;
};

const PlayerScoreCard: React.FC<PlayerScoreCardProps> = ({
    player,
    rank,
    isMe = false,
    isCompact = false,
    isDense = false,
}) => {
    const { t } = useTranslation();
    const theme = getRankTheme(rank);
    const layout = isDense
        ? DENSE_LAYOUT
        : isCompact
            ? COMPACT_LAYOUT
            : DEFAULT_LAYOUT;
    const isCondensed = isCompact || isDense;
    const groupRenders = React.useMemo(
        () => computeGroupRenders(player, layout),
        [layout, player],
    );
    const showInitialScore = player.initialScore !== 0 && player.score !== 0 && player.initialScore !== player.score;

    return (
        <div
            data-player-index={rank - 1}
            className="ge-player relative shrink-0 snap-center"
            style={{ width: layout.width, paddingTop: layout.avatarOverlap }}
        >
            {/* Paper card */}
            <div
                className={`relative flex flex-col overflow-hidden ${
                    isDense ? "rounded-2xl" : "rounded-3xl"
                }`}
                style={{
                    width: layout.width,
                    height: layout.height,
                    border: `${layout.borderWidth}px solid #ffffff`,
                    background: theme.paper,
                    boxShadow: isMe ? ME_HALO : undefined,
                }}
            >
                {/* Rank number — UNO corner style */}
                <span
                    className={`absolute z-20 font-changa-one leading-none text-white ${
                        isDense ? "top-1 left-2 text-base" : "top-1.5 left-2.5 text-xl"
                    }`}
                    style={{
                        transform: "skewX(-10deg)",
                        transformOrigin: "left top",
                        textShadow: buildRankTextShadow(theme),
                    }}
                >
                    {rank}
                </span>

                {/* Flag — top-right corner */}
                {player.flag && (
                    <img
                        src={getPublicAssetUrl(`assets/flags/${player.flag}.svg`)}
                        alt=""
                        className={`absolute z-20 object-contain rounded-sm shadow-[0_1px_2px_rgba(0,0,0,0.25)] ${
                            isDense ? "top-1.5 right-2 w-3.5 h-2.5" : "top-2 right-2.5 w-4 h-3"
                        }`}
                        draggable={false}
                    />
                )}

                {/* Name header */}
                <div
                    className={`mx-2 flex items-center justify-center shrink-0 ${
                        isDense
                            ? "px-1.5 pb-0.5"
                            : isCompact
                                ? "px-2 pb-1"
                                : "px-3 pb-1.5"
                    }`}
                    style={{
                        paddingTop: layout.avatarOverlap + (isCondensed ? 2 : 4),
                        borderBottom: `2px dotted ${theme.dividerColor}`,
                    }}
                >
                    <span className={`font-extrabold w-full min-w-0 leading-tight text-center ${
                        isCondensed
                            ? `truncate whitespace-nowrap tracking-tight ${
                                isDense ? "text-[9px]" : "text-[10px]"
                            }`
                            : "break-all line-clamp-2 text-xs"
                    } ${theme.text}`}>
                        {player.username}
                    </span>
                </div>

                {/* Body — 3 groups */}
                <div className="flex-1 mx-2 flex flex-col divide-y-2 divide-dotted">
                    {groupRenders.map((render) => (
                        <GroupSection
                            key={render.group}
                            render={render}
                            theme={theme}
                            t={t}
                            layout={layout}
                            isCompact={isCompact}
                            isDense={isDense}
                        />
                    ))}
                </div>

                {/* Total footer */}
                <div
                    className={`mx-2 px-2 flex items-center justify-between shrink-0 ${
                        isDense ? "py-0.5" : isCompact ? "py-1" : "py-1.5"
                    }`}
                    style={{ borderTop: `2px dotted ${theme.dividerColor}` }}
                >
                    <span className={`uppercase font-black tracking-[0.15em] ${
                        isDense ? "text-[8px]" : "text-[10px]"
                    } ${theme.text}`}>
                        Total
                    </span>
                    <div className="flex items-baseline gap-1.5">
                        {showInitialScore && (
                            <span className={`text-xs font-bold tabular-nums line-through opacity-50 ${theme.text}`}>
                                {-Math.abs(player.initialScore)}
                            </span>
                        )}
                        <span
                            className={`ge-score font-black tabular-nums leading-none ${
                                isDense ? "text-lg" : isCompact ? "text-xl" : "text-2xl"
                            } ${theme.text}`}
                            data-final-score={player.score}
                        >
                            {player.score > 0 ? `+${player.score}` : player.score}
                        </span>
                    </div>
                </div>
            </div>

            {/* Avatar overlap */}
            <div className="absolute left-1/2 top-0 -translate-x-1/2 z-30">
                <div className="relative">
                    <Avatar
                        src={player.avatar}
                        className={
                            isDense
                                ? "w-10 h-10"
                                : isCompact
                                    ? "w-12 h-12"
                                    : "w-14 h-14"
                        }
                    />
                    <LevelBadge
                        level={player.level}
                        className={`-bottom-0.5 -right-0.5 ${
                            isDense
                                ? "w-4 h-4 text-[8px]"
                                : isCompact
                                    ? "w-[18px] h-[18px] text-[9px]"
                                    : "w-5 h-5 text-[10px]"
                        }`}
                    />
                </div>
            </div>
        </div>
    );
};

type GroupSectionProps = {
    render: GroupRender;
    theme: RankTheme;
    t: (key: string) => string;
    layout: ScoreCardLayout;
    isCompact: boolean;
    isDense: boolean;
};

const GroupSection: React.FC<GroupSectionProps> = ({
    render,
    theme,
    t,
    layout,
    isCompact,
    isDense,
}) => {
    const { group, subtotal, isEmpty, overlap, stackWidth, items } = render;
    const textColor = isEmpty ? theme.textWeak : theme.text;

    return (
        <div
            className={`ge-group flex-1 px-2 flex flex-col justify-center ${
                isCompact || isDense ? "gap-0" : "gap-0.5"
            }`}
            style={{ borderColor: theme.dividerColor }}
        >
            <div className="flex items-center justify-between">
                <span className={`uppercase tracking-wider font-extrabold ${
                    isDense ? "text-[8px]" : "text-[9px]"
                } ${textColor}`}>
                    {t(`room.score_group_${group}`)}
                </span>
                <span
                    className={`ge-subtotal font-extrabold tabular-nums ${
                        isDense ? "text-[10px]" : isCompact ? "text-xs" : "text-sm"
                    } ${textColor}`}
                    data-final-subtotal={subtotal}
                >
                    {subtotal}
                </span>
            </div>
            <div
                className="relative"
                style={{
                    height: CARD_HEIGHT * layout.miniScale,
                    width: stackWidth,
                }}
            >
                {items.map((item) => (
                    item.kind === "card" ? (
                        <MiniCard
                            key={item.card.id}
                            card={item.card}
                            index={item.index}
                            overlap={overlap}
                            layout={layout}
                        />
                    ) : (
                        <BackMiniCard
                            key={`back-${group}`}
                            index={item.index}
                            overlap={overlap}
                            count={item.count}
                            layout={layout}
                        />
                    )
                ))}
            </div>
        </div>
    );
};

export default PlayerScoreCard;
